import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../lib/AuthContext";
import Hummingbird from "./brand/Hummingbird";
import SocialIcons from "./SocialIcons";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";

const Nav = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setUserMenu(false); }, [loc.pathname]);

  // Don't render nav on admin & auth pages (cleaner UX)
  const path = loc.pathname;
  const hideNav = path.startsWith("/admin") && path !== "/admin/login" ? false : false;

  const links = [
    { to: "/sat-master", label: "SAT Master" },
    { to: "/mentor", label: "SAT Mentor" },
    { to: "/stimmen", label: "Stimmen" },
    { to: "/herzbewegung", label: "Herzbewegung" },
    { to: "/gesellschaftsbarometer", label: "Barometer" },
    { to: "/about", label: "Über Nenad" },
    { to: "/shop", label: "Shop" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3 backdrop-blur-xl bg-[#081328]/70 border-b border-white/5" : "py-5 bg-transparent"
      }`}
    >
      <div className="container-sat flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand-link">
          <Hummingbird size={60} />
          <div className="leading-tight">
            <div className="serif text-2xl font-light tracking-wide transition group-hover:text-[#D4AF37]">SAT</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">Akademie</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
          {links.map((l) => {
            const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`nav-link ${active ? "is-active" : ""}`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* Sprachumschalter für MVL ausgeblendet — EN unvollständig */}

          {/* Auth area */}
          {user ? (
            <div className="hidden md:block relative">
              <button onClick={() => setUserMenu(!userMenu)} className="btn-secondary text-sm" data-testid="nav-user-menu">
                <User size={14} /> {user.name?.split(" ")[0] || "Account"}
              </button>
              {userMenu && (
                <div className="absolute right-0 mt-2 w-56 glass rounded-xl p-2 z-50" data-testid="nav-user-dropdown">
                  {user.role === "admin" && (
                    <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-sm"><LayoutDashboard size={14} /> Admin</Link>
                  )}
                  <Link to="/academy/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-sm"><User size={14} /> Mein Bereich</Link>
                  <Link to="/referrals" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-sm text-[#D4AF37]"><User size={14} /> SAT Partner Programm</Link>
                  <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-sm text-red-300"><LogOut size={14} /> Abmelden</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth/login" className="hidden md:inline-flex btn-ghost text-sm" data-testid="nav-login">Login</Link>
          )}

          <div className="hidden xl:flex items-center gap-2 ml-1 pl-3 border-l border-white/10">
            <SocialIcons variant="header" />
          </div>

          <Link to="/erstgespraech" className="hidden md:inline-flex btn-primary text-sm py-3 px-5" data-testid="nav-cta-erstgespraech">
            Kostenloses Erstgespräch
          </Link>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-white/80" aria-label="menu" data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 glass rounded-2xl p-5 space-y-1" data-testid="nav-mobile-panel">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block py-3 px-3 text-white/80 hover:text-white border-b border-white/5">{l.label}</Link>
          ))}
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" className="block py-3 px-3 text-[#D4AF37]">Admin</Link>}
              <Link to="/academy/dashboard" className="block py-3 px-3 text-white/80">Mein Bereich</Link>
              <button onClick={() => { logout(); nav("/"); }} className="block py-3 px-3 text-red-300 w-full text-left">Abmelden</button>
            </>
          ) : (
            <Link to="/auth/login" className="block py-3 px-3 text-white/80">Login</Link>
          )}
          <div className="flex items-center justify-end pt-3">
            <Link to="/erstgespraech" className="btn-primary text-sm py-3 px-5">Kostenloses Erstgespräch</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
